import React, { useEffect, useState } from "react";
import { GraduationCap, Percent, ArrowsClockwise, Coins, ChartLine, UserPlus, Eye, EyeSlash, DownloadSimple } from "@phosphor-icons/react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { roleLabel } from "@/lib/roles";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const userMatches = (c, q) => !q || `${c.name || ""} ${c.email || ""}`.toLowerCase().includes(q.toLowerCase());

function ShareSettingsCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/profit-share-settings").then(r => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const adminPct = Math.round((100 - (Number(s.l1_percent) || 0) - (Number(s.l2_percent) || 0)) * 100) / 100;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/profit-share-settings", {
        l1_percent: Number(s.l1_percent), l2_percent: Number(s.l2_percent),
        referral_cookie_days: Number(s.referral_cookie_days),
      });
      toast.success("Profit-share defaults saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="profit-share-settings-card">
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><Percent size={20} weight="duotone" /> Profit-share defaults</h3>
      <p className="text-xs text-[#000000] mb-3">Applied to every new attributed sale (post-discount price). Per-instructor overrides win.</p>
      <div className="flex flex-wrap gap-4 items-end">
        {[["L1 share %", "l1_percent", "share-l1-input"], ["L2 share %", "l2_percent", "share-l2-input"],
          ["Referral cookie (days)", "referral_cookie_days", "cookie-days-input"]].map(([label, key, tid]) => (
          <label key={key} className="text-xs font-semibold text-[#000000]">
            {label}
            <input data-testid={tid} type="number" value={s[key]} min={0}
              onChange={e => setS({ ...s, [key]: e.target.value })}
              className="block mt-1 w-28 rounded-xl border border-[#C4C8CE] p-2 text-sm" />
          </label>
        ))}
        <div className="text-xs font-semibold text-[#000000]" data-testid="admin-share-derived">
          Your share<div className="mt-1 px-3 py-2 rounded-xl bg-[#F4F4F5] font-display font-extrabold text-base">{adminPct}%</div>
        </div>
        <button data-testid="share-settings-save" className="btn-primary" disabled={saving} onClick={save}>Save</button>
      </div>
    </div>
  );
}

function InstructorSettingsDialog({ inst, onClose, onSaved }) {
  const [l1, setL1] = useState(inst?.share_l1_override ?? "");
  const [l2, setL2] = useState(inst?.share_l2_override ?? "");
  const [cookie, setCookie] = useState(inst?.referral_cookie_days_override ?? "");
  const [hidden, setHidden] = useState(!!inst?.lineage_hidden);
  if (!inst) return null;
  const save = async () => {
    try {
      const body = { lineage_hidden: hidden };
      if (l1 === "" && l2 === "" && cookie === "") body.clear_overrides = true;
      if (l1 !== "") body.share_l1_override = Number(l1);
      if (l2 !== "") body.share_l2_override = Number(l2);
      if (cookie !== "") body.referral_cookie_days_override = Number(cookie);
      await api.patch(`/admin/instructors/${inst.id}`, body);
      toast.success("Instructor settings saved");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-white" aria-describedby={undefined} data-testid="instructor-settings-dialog">
        <DialogHeader><DialogTitle className="font-display">{inst.name} — overrides</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block text-xs font-semibold text-[#000000]">L1 share override % (blank = global default)
            <input data-testid="override-l1-input" type="number" value={l1} onChange={e => setL1(e.target.value)}
              className="block mt-1 w-full rounded-xl border border-[#C4C8CE] p-2" /></label>
          <label className="block text-xs font-semibold text-[#000000]">L2 share override %
            <input data-testid="override-l2-input" type="number" value={l2} onChange={e => setL2(e.target.value)}
              className="block mt-1 w-full rounded-xl border border-[#C4C8CE] p-2" /></label>
          <label className="block text-xs font-semibold text-[#000000]">Referral cookie days override
            <input data-testid="override-cookie-input" type="number" value={cookie} onChange={e => setCookie(e.target.value)}
              className="block mt-1 w-full rounded-xl border border-[#C4C8CE] p-2" /></label>
          <label className="flex items-center gap-2 text-xs font-semibold text-[#000000] cursor-pointer">
            <input data-testid="lineage-hidden-checkbox" type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />
            Hide public lineage ("Certified by …")
          </label>
          <button data-testid="instructor-settings-save" className="btn-primary w-full" onClick={save}>Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecalcDialog({ att, onClose, onSaved }) {
  const [l1, setL1] = useState(att?.l1_pct ?? 0);
  const [l2, setL2] = useState(att?.l2_pct ?? 0);
  const [reason, setReason] = useState("");
  if (!att) return null;
  const go = async () => {
    try {
      await api.post(`/admin/attributions/${att.id}/recalculate`, {
        l1_percent: att.l1_id ? Number(l1) : null, l2_percent: att.l2_id ? Number(l2) : null,
        reason: reason.trim(),
      });
      toast.success("Sale recalculated");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-white" aria-describedby={undefined} data-testid="recalc-dialog">
        <DialogHeader><DialogTitle className="font-display">Recalculate this sale</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-[#000000]">{att.course_title} · {money(att.sale_cents)}</div>
          {att.l1_id && <label className="block text-xs font-semibold">L1 % ({att.l1_name})
            <input data-testid="recalc-l1-input" type="number" value={l1} onChange={e => setL1(e.target.value)}
              className="block mt-1 w-full rounded-xl border border-[#C4C8CE] p-2" /></label>}
          {att.l2_id && <label className="block text-xs font-semibold">L2 % ({att.l2_name})
            <input data-testid="recalc-l2-input" type="number" value={l2} onChange={e => setL2(e.target.value)}
              className="block mt-1 w-full rounded-xl border border-[#C4C8CE] p-2" /></label>}
          <textarea data-testid="recalc-reason-input" value={reason} onChange={e => setReason(e.target.value)} rows={2}
            placeholder="Reason (audit-logged)" className="w-full rounded-xl border border-[#C4C8CE] p-2" />
          <button data-testid="recalc-confirm" className="btn-primary w-full" disabled={!reason.trim()} onClick={go}>Recalculate</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function InstructorsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const isL2 = user?.role === "level2_instructor";
  const [data, setData] = useState(null);
  const [certifyId, setCertifyId] = useState("");
  const [certifyLevel, setCertifyLevel] = useState("1");
  const [traineeId, setTraineeId] = useState("");
  const [editInst, setEditInst] = useState(null);
  const [atts, setAtts] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [recalcAtt, setRecalcAtt] = useState(null);
  const [manualTxn, setManualTxn] = useState("");
  const [manualInst, setManualInst] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [myTrainees, setMyTrainees] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [certifySearch, setCertifySearch] = useState("");
  const [traineeSearch, setTraineeSearch] = useState("");

  const load = () => {
    if (isAdmin || isL2) api.get("/admin/instructors").then(r => setData(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/admin/attributions").then(r => setAtts(r.data)).catch(() => {});
      api.get("/admin/payouts").then(r => setPayouts(r.data)).catch(() => {});
    }
    api.get("/instructor/my-trainees").then(r => setMyTrainees(r.data)).catch(() => {});
    api.get("/instructor/trainee-candidates").then(r => setCandidates(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line

  const certify = async (uid, level) => {
    try {
      const { data: d } = await api.post(`/instructors/${uid}/certify`, { level: Number(level) });
      toast.success(`Certified as Level ${level} — recert due ${d.recert_expires_on}`);
      setCertifyId("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const recertify = async (uid) => {
    try {
      const { data: d } = await api.post(`/instructors/${uid}/recertify`);
      toast.success(`Recertified — now valid until ${d.recert_expires_on}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const registerTrainee = async () => {
    try {
      await api.post("/instructors/trainees", { user_id: traineeId });
      toast.success("Trainee registered — 1-year certification window started");
      setTraineeId("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const runPayouts = async () => {
    if (!window.confirm("Run payouts now? Every instructor with a positive pending balance gets a statement and their balance resets.")) return;
    try {
      const { data: d } = await api.post("/admin/payouts/run");
      toast.success(d.payouts.length ? `${d.payouts.length} payout(s) created` : "Nothing pending to pay out");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const manualAttribute = async () => {
    try {
      await api.post("/admin/attributions/manual", {
        transaction_id: manualTxn.trim(), instructor_id: manualInst, reason: manualReason.trim(),
      });
      toast.success("Sale attributed");
      setManualTxn(""); setManualInst(""); setManualReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const recertChip = (i) => i.recert_status === "expired"
    ? <span className="px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#991B1B] text-[10px] font-bold">EXPIRED {i.recert_expires_on}</span>
    : i.recert_status === "active"
      ? <span className="px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534] text-[10px] font-bold">until {i.recert_expires_on}</span>
      : <span className="px-1.5 py-0.5 rounded bg-[#F4F4F5] text-[#000000] text-[10px] font-bold">no expiry</span>;

  return (
    <div className="space-y-5">
      {isAdmin && <ShareSettingsCard />}

      {(isAdmin || isL2) && data && (
        <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="instructors-list-card">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><GraduationCap size={20} weight="duotone" /> Instructors</h3>
          {data.instructors.length === 0 ? (
            <p className="text-sm text-[#000000] mt-2">No certified instructors yet — certify one below.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="instructors-table">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[#000000] border-b border-[#EDEEF0]">
                  <th className="py-1.5 pr-2">Name</th><th className="pr-2">Level</th><th className="pr-2">Certified by</th>
                  <th className="pr-2">Recert</th><th className="pr-2">Code</th><th className="pr-2">Overrides</th>
                  <th className="pr-2 text-right">Pending</th><th></th>
                </tr></thead>
                <tbody>
                  {data.instructors.map(i => (
                    <tr key={i.id} className="border-b border-[#F4F4F5]" data-testid={`instructor-row-${i.id}`}>
                      <td className="py-2 pr-2 font-semibold">{i.name}
                        {i.lineage_hidden && <EyeSlash size={12} className="inline ml-1 text-[#000000]" title="Lineage hidden" />}</td>
                      <td className="pr-2 font-display font-bold">L{i.level}</td>
                      <td className="pr-2 text-[#000000]">{i.certified_by_name || "—"}</td>
                      <td className="pr-2">{recertChip(i)}</td>
                      <td className="pr-2 font-mono text-xs">{i.referral_code || "—"}</td>
                      <td className="pr-2 text-xs text-[#000000]">
                        {i.share_l1_override != null && <span>L1 {i.share_l1_override}% </span>}
                        {i.share_l2_override != null && <span>L2 {i.share_l2_override}% </span>}
                        {i.referral_cookie_days_override != null && <span>{i.referral_cookie_days_override}d cookie</span>}
                        {i.share_l1_override == null && i.share_l2_override == null && i.referral_cookie_days_override == null && "defaults"}
                      </td>
                      <td className="pr-2 text-right font-semibold">{money(i.payout_balance_cents)}</td>
                      <td className="whitespace-nowrap">
                        <button data-testid={`recertify-${i.id}`} className="btn-ghost text-xs text-[#166534] flex-inline items-center gap-0.5"
                          onClick={() => recertify(i.id)} title="Extend certification one year">
                          <ArrowsClockwise size={12} className="inline" /> Recertify
                        </button>
                        {isAdmin && (
                          <button data-testid={`edit-instructor-${i.id}`} className="btn-ghost text-xs text-[#000000] ml-1"
                            onClick={() => setEditInst(i)}>Edit</button>
                        )}
                        {isAdmin && i.level === 1 && (
                          <button data-testid={`promote-l2-${i.id}`} className="btn-ghost text-xs text-[#000000] ml-1"
                            onClick={() => certify(i.id, 2)} title="Promote to Level 2">→ L2</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-[#EDEEF0] flex flex-wrap items-end gap-2">
            <label className="text-xs font-semibold text-[#000000]">Search
              <div className="mt-1">
                <input data-testid="certify-candidate-search" className="input-base w-48 text-sm" placeholder="Name or email…"
                  value={certifySearch} onChange={(e) => setCertifySearch(e.target.value)} />
              </div>
            </label>
            <label className="text-xs font-semibold text-[#000000]">Certify a user
              <div className="mt-1"><Select value={certifyId} onValueChange={setCertifyId}>
                <SelectTrigger data-testid="certify-candidate-select" className="w-64"><SelectValue placeholder="Pick a user…" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {[...(data.trainees || []), ...(data.candidates || [])].filter(c => userMatches(c, certifySearch)).slice(0, 100).map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                      {c.trainee_of_name ? ` · trainee of ${c.trainee_of_name}` : (c.role ? ` · ${roleLabel(c.role)}` : "")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
            </label>
            <label className="text-xs font-semibold text-[#000000]">Level
              <div className="mt-1"><Select value={certifyLevel} onValueChange={setCertifyLevel}>
                <SelectTrigger data-testid="certify-level-select" className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Level 1</SelectItem>
                  {isAdmin && <SelectItem value="2">Level 2</SelectItem>}
                </SelectContent>
              </Select></div>
            </label>
            <button data-testid="certify-button" className="btn-primary" disabled={!certifyId}
              onClick={() => certify(certifyId, certifyLevel)}>Certify</button>
          </div>

          {data.trainees?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#EDEEF0]">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#000000] mb-1.5">L1 Trainees (1-year certification window)</div>
              {data.trainees.map(t => (
                <div key={t.id} className="flex items-center gap-2 text-sm py-1" data-testid={`trainee-row-${t.id}`}>
                  <span className="font-semibold">{t.name}</span>
                  <span className="text-xs text-[#000000]">trainee of {t.trainee_of_name || "—"} · window ends {t.trainee_window_ends}</span>
                  {t.lapsed && <span className="px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#991B1B] text-[10px] font-bold">LAPSED — must restart</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* L1: register a trainee */}
      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="trainees-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><UserPlus size={20} weight="duotone" /> My trainees</h3>
        <p className="text-xs text-[#000000] mb-2">You can train a candidate toward Level 1 — only a Level 2 Instructor (or the Super Admin) can issue the actual certification. Trainees get a 1-year window.</p>
        {myTrainees.length === 0 ? <p className="text-sm text-[#000000]">No trainees yet.</p> : myTrainees.map(t => (
          <div key={t.id} className="flex items-center gap-2 text-sm py-1" data-testid={`my-trainee-${t.id}`}>
            <span className="font-semibold">{t.name}</span>
            <span className="text-xs text-[#000000]">window ends {t.trainee_window_ends}</span>
            {t.certified && <span className="px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534] text-[10px] font-bold">CERTIFIED</span>}
            {t.lapsed && !t.certified && <span className="px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#991B1B] text-[10px] font-bold">LAPSED</span>}
          </div>
        ))}
        {(isAdmin || isL2 || user?.role === "level1_instructor") && candidates.length > 0 && (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <input data-testid="trainee-candidate-search" className="input-base text-sm" style={{width:"12rem"}} placeholder="Search name or email…"
              value={traineeSearch} onChange={(e) => setTraineeSearch(e.target.value)} />
            <Select value={traineeId} onValueChange={setTraineeId}>
              <SelectTrigger data-testid="trainee-candidate-select" className="w-64"><SelectValue placeholder="Register a trainee…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {candidates.filter(c => userMatches(c, traineeSearch)).slice(0, 100).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.role ? ` · ${roleLabel(c.role)}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button data-testid="register-trainee-button" className="btn-secondary" disabled={!traineeId} onClick={registerTrainee}>Register</button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="payouts-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Coins size={20} weight="fill" color="#FFC400" /> Payouts</h3>
            <button data-testid="run-payouts-button" className="btn-primary" onClick={runPayouts}>Run payouts now</button>
          </div>
          <p className="text-xs text-[#000000] mt-1">Monthly schedule — every positive pending balance becomes an itemized statement. Negative (clawback) balances carry forward.</p>
          {payouts.length === 0 ? <p className="text-sm text-[#000000] mt-2">No payouts yet.</p> : (
            <div className="mt-2">
              {payouts.map(p => (
                <div key={p.id} className="flex items-center justify-between text-sm py-1.5 border-b border-[#F4F4F5]" data-testid={`payout-row-${p.id}`}>
                  <span>{p.period} · <b>{p.instructor_name}</b> · {p.line_count} line{p.line_count === 1 ? "" : "s"}</span>
                  <span className="flex items-center gap-2 font-bold">{money(p.amount_cents)}
                    <a href={`${API_BASE}/payouts/${p.id}/pdf`} target="_blank" rel="noreferrer" className="text-[#000000]" data-testid={`payout-pdf-${p.id}`}><DownloadSimple size={14} /></a>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isAdmin && atts && (
        <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="attributions-card">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><ChartLine size={20} weight="duotone" /> Attribution analytics</h3>
          {atts.top_instructors.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {atts.top_instructors.slice(0, 6).map(t => (
                <div key={t.instructor_id} className="rounded-xl border border-[#EDEEF0] bg-[#F9FAFB] px-3 py-2 text-xs" data-testid={`top-instructor-${t.instructor_id}`}>
                  <div className="font-bold">{t.name}</div>
                  <div className="text-[#000000]">{t.sales} sale{t.sales === 1 ? "" : "s"} · earned {money(t.earned_cents)}</div>
                </div>
              ))}
            </div>
          )}
          {atts.attributions.length === 0 ? <p className="text-sm text-[#000000] mt-2">No attributed sales yet.</p> : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="attributions-table">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[#000000] border-b border-[#EDEEF0]">
                  <th className="py-1.5 pr-2">Date</th><th className="pr-2">Course</th><th className="pr-2">Sale</th>
                  <th className="pr-2">Source</th><th className="pr-2">Chain</th><th className="pr-2">You keep</th><th></th>
                </tr></thead>
                <tbody>
                  {atts.attributions.slice(0, 30).map(a => (
                    <tr key={a.id} className={`border-b border-[#F4F4F5] ${a.clawed_back ? "opacity-50 line-through" : ""}`} data-testid={`attribution-row-${a.id}`}>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{a.created_at?.slice(0, 10)}</td>
                      <td className="pr-2 max-w-[160px] truncate">{a.course_title || "—"}</td>
                      <td className="pr-2">{money(a.sale_cents)}</td>
                      <td className="pr-2 text-xs">{a.source}{a.promo_code ? ` (${a.promo_code})` : ""}</td>
                      <td className="pr-2 text-xs">
                        {a.l1_id && <span>L1 {a.l1_name} {a.l1_pct}% = {money(a.l1_cents)}</span>}
                        {a.l1_id && a.l2_id && " · "}
                        {a.l2_id && <span>L2 {a.l2_name} {a.l2_pct}% = {money(a.l2_cents)}</span>}
                      </td>
                      <td className="pr-2 font-semibold">{money(a.admin_cents)}</td>
                      <td>
                        {!a.clawed_back && (
                          <button data-testid={`recalc-${a.id}`} className="btn-ghost text-xs text-[#000000]" onClick={() => setRecalcAtt(a)}>Recalc</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-[#EDEEF0]">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#000000] mb-1.5">Manual attribution (retroactive)</div>
            <div className="flex flex-wrap items-end gap-2">
              <input data-testid="manual-txn-input" value={manualTxn} onChange={e => setManualTxn(e.target.value)}
                placeholder="Transaction or session ID" className="rounded-xl border border-[#C4C8CE] p-2 text-sm w-64" />
              <Select value={manualInst} onValueChange={setManualInst}>
                <SelectTrigger data-testid="manual-instructor-select" className="w-48"><SelectValue placeholder="Instructor…" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {(data?.instructors || []).map(i => <SelectItem key={i.id} value={i.id}>{i.name} (L{i.level})</SelectItem>)}
                </SelectContent>
              </Select>
              <input data-testid="manual-reason-input" value={manualReason} onChange={e => setManualReason(e.target.value)}
                placeholder="Reason (audit-logged)" className="rounded-xl border border-[#C4C8CE] p-2 text-sm w-56" />
              <button data-testid="manual-attribute-button" className="btn-secondary"
                disabled={!manualTxn.trim() || !manualInst || !manualReason.trim()} onClick={manualAttribute}>Attribute</button>
            </div>
          </div>
        </div>
      )}

      {editInst && <InstructorSettingsDialog inst={editInst} onClose={() => setEditInst(null)} onSaved={load} />}
      {recalcAtt && <RecalcDialog att={recalcAtt} onClose={() => setRecalcAtt(null)} onSaved={load} />}
    </div>
  );
}
